import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Upload, Trash, Loader2 } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { CountrySelect } from "@/components/ui/country-select";
import { Separator } from "@/components/ui/separator";
import { ContactForm } from "@/components/merchant/ContactForm";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { BUSINESS_TYPE } from "@/constants";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import countries from "@/constants/array/country_city.json";
import { MerchantApi } from "@/api";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";

// Define CURRENCY_OPTIONS directly here
const CURRENCY_OPTIONS = [
  { id: 1, label: "United State Dollar", value: "USD" },
  { id: 2, label: "EURO", value: "EUR" },
  { id: 3, label: "British Pound", value: "GBP" },
  { id: 4, label: "Indian Rupee", value: "INR" },
  { id: 5, label: "United Arab Emirates Dirham", value: "AED" },
  { id: 6, label: "Ethiopian Birr", value: "ETB" },
];

const MerchantSchema = Yup.object().shape({
  businessName: Yup.string().required("Merchant Name is required"),
  merchantCode: Yup.string().required("Merchant code is required."),
  businessLocation: Yup.string(),
  businessType: Yup.string().required("Business type is required."),
  businessDescription: Yup.string().required(
    "Merchant description is required"
  ),
  websiteUrl: Yup.string()
    .required("Website URL is required")
    .url("Website URL must be a valid URL format"),
  offerDomain: Yup.string().when("businessType", {
    is: BUSINESS_TYPE.OFFLINE,
    then: (schema) =>
      schema.required("Offer Domain is required for offline merchants"),
    otherwise: (schema) => schema.optional(),
  }),
  country: Yup.string(),
  city: Yup.string(),
  taxId: Yup.string(),
  currencyVO: Yup.string().required("Payment currency is required"),
  refundPeriodInDays: Yup.string().required("Refund Period is required"),

  categoryVOList: Yup.array()
    .of(
      Yup.object().shape({
        categoryCode: Yup.string(),
        categoryName: Yup.string().required("Category name is required"),
        cashbackPercentage: Yup.number()
          .typeError("Cashback percentage should be a number")
          .required("Cashback percentage is required")
          .positive("Cashback percentage must be greater than 0")
          .min(0.01, "Cashback percentage must be greater than 0")
          .max(100, "Cashback percentage cannot exceed 100%"),
      })
    )
    .min(1, "At least one category is required"),
  contacts: Yup.array()
    .min(1, "At least one contact is required")
    .of(
      Yup.object().shape({
        contactName: Yup.string()
          .required("Contact name is required")
          .matches(/^[a-zA-Z\s]+$/, "Name can only contain letters and spaces"),
        contactEmail: Yup.string()
          .required("Email is required")
          .email("Email must be a valid email address"),
        contactRole: Yup.string()
          .required("Role is required")
          .matches(/^[a-zA-Z\s]+$/, "Role can only contain letters and spaces"),
        countryCode: Yup.string().required("Country code is required"),
        phoneNumber: Yup.string()
          .required("Phone number is required")
          .matches(/^\d+$/, "Phone number can only contain positive numbers")
          .min(7, "Phone number must be at least 7 digits")
          .max(15, "Phone number cannot exceed 15 digits"),
      })
    ),
});

const CreateMerchant = ({
  basePath = "/accesspoint",
}: {
  basePath?: string;
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [merchantLogo, setMerchantLogo] = useState();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [businessType, setBusinessType] = useState(BUSINESS_TYPE.OFFLINE);
  const [showLogoError, setShowLogoError] = useState(false);
  const form = useForm({
    resolver: yupResolver(MerchantSchema),
    mode: "onSubmit",
    defaultValues: {
      businessName: "",
      merchantCode: "",

      businessDescription: "",
      businessType: BUSINESS_TYPE.OFFLINE,
      country: "",
      city: "",
      currencyVO: "",
      taxId: "",
      refundPeriodInDays: "",
      websiteUrl: "",
      offerDomain: "",
      businessLocation: "",
      categoryVOList: [
        { categoryCode: "", categoryName: "", cashbackPercentage: 0 },
      ],
      contacts: [
        {
          contactName: "",
          contactEmail: "",
          countryCode: "+1",
          phoneNumber: "",
          contactRole: "",
        },
      ],
    },
  });

  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory,
  } = useFieldArray({
    control: form.control,
    name: "categoryVOList",
  });

  // Clear offerDomain validation when switching to online (since field becomes optional)
  useEffect(() => {
    if (businessType === BUSINESS_TYPE.ONLINE) {
      form.clearErrors("offerDomain");
    }
  }, [businessType, form]);

  const handleBrandLogoChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const image = new Image();
      image.onload = () => {
        setMerchantLogo(selectedFile);
        setShowLogoError(false); // Clear error when logo is uploaded
      };
      image.src = URL.createObjectURL(selectedFile);
    }
  };

  // Function to check logo before form submission attempt
  const handleSubmitClick = () => {
    if (!merchantLogo) {
      setShowLogoError(true);
      toast.error("Merchant logo is required");
    }
  };

  const handleSubmit = async (formData: any) => {
    // Check merchant logo first (before other validations)
    if (!merchantLogo) {
      setShowLogoError(true);
      toast.error("Merchant logo is required");
      return;
    }

    // Trigger validation for all fields before submission
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error("Please fix validation errors before submitting");
      return;
    }

    setIsLoading(true);
    try {
      // Get username from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userName = parsedUserInfo?.username || "";

      const { contacts, ...businessData } = formData;
      const data = {
        ...businessData,
        currencyVO: {
          id: +formData?.currencyVO,
        },
        createdDateInMillis: new Date().getTime(),
        merchantName: formData?.businessName,
        country: selectedCountry?.name,
        categoryVOList: formData?.categoryVOList.map((category) => ({
          ...category,
          cashbackPercentage: +category.cashbackPercentage,
        })),
        userName: userName,
      };

      console.log("data", data);
      const form = new FormData();
      if (merchantLogo) {
        form.append("merchantLogo", merchantLogo);
      }
      const blob = new Blob([JSON.stringify(data)], {
        type: "application/json",
      });
      form.append("merchantVO", blob);
      const res = await MerchantApi.createUpdateMerchant(form);
      // Create contacts after merchant is created
      if (res?.data?.id && contacts?.length) {
        await Promise.all(
          contacts.map((contact) =>
            MerchantApi.createOrUpdateMerchantContact({
              merchantId: res.data.id,
              contactName: contact.contactName,
              contactEmail: contact.contactEmail,
              contactRole: contact.contactRole,
              merchantContactPhoneVOList: contact.phoneNumber
                ? [
                    {
                      phoneCountryCode: contact.countryCode || "",
                      phoneNumber: contact.phoneNumber,
                    },
                  ]
                : [],
            })
          )
        );
      }
      toast.success("Merchant created successfully");
      navigate(`${basePath}/merchants`);
    } catch (err) {
      toast.error(err?.message || "Failed to create merchat");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to={`${basePath}/merchants`}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Create Merchant</h1>
          <p className="text-gray-500">Add a new merchant to your platform</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <FormLabel>Merchant Logo*</FormLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleBrandLogoChange}
                  />
                  <div
                    className={`w-24 h-24 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed cursor-pointer hover:bg-gray-50 transition-all ${
                      showLogoError && !merchantLogo
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                    onClick={() => {
                      console.log("CLICKED", fileInputRef?.current);
                      fileInputRef?.current?.click();
                    }}
                  >
                    {merchantLogo ? (
                      <img
                        src={URL.createObjectURL(merchantLogo)}
                        alt={form?.getValues("businessName") || "Business Logo"}
                        className="max-h-16 max-w-16"
                        id="fileInput"
                      />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1">
                          Upload Logo
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Recommended: 400x400px, max 2MB
                  </p>
                  {showLogoError && !merchantLogo && (
                    <p className="text-sm text-red-600">
                      Merchant logo is required
                    </p>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant name" {...field} />
                      </FormControl>
                      <FormDescription>
                        The official name of the merchant
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="merchantCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Code*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center justify-between">
                  <FormLabel>Merchant Type</FormLabel>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="merchant-type"
                      checked={businessType === BUSINESS_TYPE.ONLINE}
                      onCheckedChange={(checked) => {
                        const newType = checked
                          ? BUSINESS_TYPE.ONLINE
                          : BUSINESS_TYPE.OFFLINE;
                        setBusinessType(newType);
                        form.setValue("businessType", newType);
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {businessType === BUSINESS_TYPE.ONLINE
                        ? "Online"
                        : "Offline"}
                    </span>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="businessDescription"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Description*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter merchant description"
                          {...field}
                          className={`min-h-[100px] ${
                            fieldState.error ? "border-red-500" : ""
                          }`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category*</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        >
                          <option value="">Select a category</option>
                          <option value="retail">Retail</option>
                          <option value="food">Food & Beverage</option>
                          <option value="travel">Travel</option>
                          <option value="wellness">Health & Wellness</option>
                          <option value="electronics">Electronics</option>
                          <option value="fashion">Fashion</option>
                          <option value="groceries">Groceries</option>
                          <option value="entertainment">Entertainment</option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Website URL*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com"
                          className={fieldState.error ? "border-red-500" : ""}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {businessType === BUSINESS_TYPE.OFFLINE && (
                  <FormField
                    control={form.control}
                    name="offerDomain"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Offer Domain*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://offers.example.com"
                            {...field}
                            className={fieldState.error ? "border-red-500" : ""}
                          />
                        </FormControl>
                        <FormDescription>
                          The domain where offers will be hosted for this
                          offline merchant
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            {/* Business & Financial Details */}
            <div className="space-y-8">
              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Business Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div className="flex items-center h-full">
                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <FormLabel>Country</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                const selected = JSON.parse(value);
                                setSelectedCountry(selected);
                                field.onChange(value);
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Country" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {(countries as any)?.map((country) => (
                                  <SelectItem
                                    key={country?.name}
                                    value={JSON.stringify(country)}
                                  >
                                    {country?.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex items-center h-full">
                      <div className="w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          City
                        </label>
                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <VirtualizedCitySelect
                              cities={selectedCountry?.cities || []}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select City"
                              disabled={!selectedCountry?.cities?.length}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="businessLocation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Location</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter location" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="taxId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT Number</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter VAT number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Financial Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <FormField
                    control={form.control}
                    name="currencyVO"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Currency*</FormLabel>
                        <FormControl>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          >
                            <option value="" disabled>
                              Select currency
                            </option>
                            {CURRENCY_OPTIONS.map((currency) => (
                              <option key={currency.id} value={currency.id}>
                                {currency.value} - {currency.label}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="refundPeriodInDays"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Refund Period (days)*</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            className={fieldState.error ? "border-red-500" : ""}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Number of days customers can request refunds
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Information */}
          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Contact Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Add one or more contact persons for this merchant
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <ContactForm form={form} />
              {form.formState.errors.contacts && (
                <p className="text-sm text-red-600">
                  {form.formState.errors.contacts.message}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {categoryFields.map((field, index) => (
                <div key={field.id} className="flex items-end gap-4">
                  <FormField
                    control={form.control}
                    name={`categoryVOList.${index}.categoryName`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Category Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Category Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`categoryVOList.${index}.cashbackPercentage`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Cashback %</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.01"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              // Prevent negative sign, 'e', 'E', '+'
                              if (["-", "e", "E", "+"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only allow numbers between 0-100
                              if (
                                value === "" ||
                                (parseFloat(value) >= 0 &&
                                  parseFloat(value) <= 100)
                              ) {
                                field.onChange(value);
                              }
                            }}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mb-1 text-red-600 hover:bg-red-100"
                    onClick={() => removeCategory(index)}
                    disabled={categoryFields.length === 1}
                    aria-label="Delete Category"
                  >
                    <Trash className="h-5 w-5" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  appendCategory({
                    categoryCode: "",
                    categoryName: "",
                    cashbackPercentage: 0,
                  })
                }
              >
                Add Category
              </Button>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" asChild>
              <Link to={`${basePath}/merchants`}>Cancel</Link>
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              onClick={handleSubmitClick}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isLoading ? "Creating..." : "Create Merchant"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CreateMerchant;
